/**
 * Lottie Service
 * Handles Lottie animation search via backend API proxy (to avoid CORS issues)
 */

import axios from 'axios';
import { getAuthToken } from './firebase';

// Configure axios for lottie API
const getApiBaseUrl = () => {
  // @ts-ignore - window.__ENV__ is injected at runtime by Docker entrypoint
  const runtimeApiUrl = (window as any).__ENV__?.API_URL;
  const buildTimeApiUrl = import.meta.env.VITE_API_URL;

  if (runtimeApiUrl) {
    return `${runtimeApiUrl}/api`;
  }
  if (buildTimeApiUrl && buildTimeApiUrl !== '/api') {
    return `${buildTimeApiUrl}/api`;
  }
  return '/api';
};

const lottieApi = axios.create({
  timeout: 30000, // 30 seconds for lottie searches
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
lottieApi.interceptors.request.use(
  async (config) => {
    config.baseURL = getApiBaseUrl();

    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Individual Lottie animation result
 */
export interface LottieResult {
  id: string;
  name: string;
  jsonUrl: string;      // Direct JSON URL for embedding
  lottieUrl: string | null;
  previewGif: string;   // GIF preview URL for grid display
  previewImage: string; // Static image fallback
  tags: string[];
  dimensions: { width: number; height: number } | null;
  author: string;
  downloadCount: number;
}

/**
 * Lottie search response
 */
export interface LottieSearchResponse {
  success: boolean;
  query: string;
  totalMatches: number;
  animations: LottieResult[];
  page?: number;
  hasMore?: boolean;
  error?: string;
}

/**
 * Lottie Service class
 */
class LottieService {
  /**
   * Search for Lottie animations
   * @param query Search query string
   * @param limit Number of results per page (default 20, max 40)
   * @param page Page number for pagination (default 1)
   */
  async searchAnimations(
    query: string,
    limit: number = 20,
    page: number = 1
  ): Promise<LottieSearchResponse> {
    if (!query.trim()) {
      return this.getPopularAnimations();
    }

    try {
      const response = await lottieApi.post('/lottie/search', {
        query,
        limit,
        page
      });
      return response.data;
    } catch (error) {
      console.error('[LottieService] Search error:', error);
      return {
        success: false,
        query,
        totalMatches: 0,
        animations: [],
        page,
        hasMore: false,
        error: error instanceof Error ? error.message : 'Lottie search failed'
      };
    }
  }

  /**
   * Get popular/trending animations for empty state
   */
  async getPopularAnimations(): Promise<LottieSearchResponse> {
    try {
      const response = await lottieApi.get('/lottie/popular');
      return response.data;
    } catch (error) {
      console.error('[LottieService] Popular animations error:', error);
      return {
        success: false,
        query: 'popular',
        totalMatches: 0,
        animations: [],
        error: error instanceof Error ? error.message : 'Failed to fetch popular animations'
      };
    }
  }
}

export const lottieService = new LottieService();
